/**
 * =========================================================================================
 *     Copyright © 2026 Dobler Data Solutions, LLC. ALL RIGHTS RESERVED.
 * =========================================================================================
 *     File            :   collectionFixRecipes.ts
 *     Feature         :   265 - Collection Integrity · FR-027/028
 *     Description     :   Turns a classified collection failure into a RUNNABLE fix.
 *
 *  🛑 WHY THIS EXISTS — the client cannot change our code, only their tenant.
 *
 *     Every collection outage this product has had traced back to a permission that was
 *     never granted. We document what to configure and then trust it was done; we never
 *     check, and we never report the gap. From the client's side the software simply
 *     produces a broken governance model, backups founded on a false premise, and nothing
 *     they can act on — so they leave.
 *
 *     The leverage here is that this system can get anyone to the right combination of
 *     permissions WITHOUT their being an Azure expert. That only works if the output is
 *     specific enough to execute. "Check permissions" is not a remedy; a command with the
 *     real identity, the real scope and the real role is.
 *
 *  🛑 AND IT MUST NOT BLUFF. A confidently wrong command is worse than none — it sends an
 *     operator to change something that was never broken, and the real gap survives with
 *     one more person convinced it was already handled. So every value that could not be
 *     read from the failure itself is emitted as an explicit `<placeholder>` and flagged,
 *     never guessed. Where no command can do the job (a Fabric tenant setting), that is
 *     stated as portal-only rather than dressed up as automation.
 *
 *     Author          :   Peter Dobler
 *     Created         :   2026-08-01
 * =========================================================================================
 */

export interface FixRecipe {
  /**
   * WHO CAN ACT ON THIS.
   *
   * 🛑 `client` ONLY when the fix is a configuration change in the customer's own tenant —
   *    a grant, a secret, a policy, a tenant setting. Everything else is `support`: a
   *    defect in our collector, an API that changed shape, a stale work-list, throttling,
   *    or an unrecognised message. None of those are the customer's to fix, and showing
   *    them commands invites someone to change infrastructure to chase a fault that was
   *    never on their side — the most expensive kind of wrong advice, because it looks
   *    like progress. Support-audience recipes therefore carry NO commands; they carry
   *    the evidence and a route to the development team.
   *
   * 🛑 THE SERVICE ACCOUNT IS OURS. We ship `fabric-control-svc` and we hold its secret in
   *    our own Key Vault, so a rejected, expired or Conditional-Access-blocked credential
   *    is OUR setup failing — not something the customer misconfigured. Owner's rule:
   *    "we have the service account and this error should not happen. If the user doesn't
   *    have the service account, then yes, this is a user error worth displaying. Anything
   *    else is on us." So `client` is reserved for the identity not being PROVISIONED,
   *    CONSENTED or GRANTED — the onboarding steps only their directory admin can perform.
   *
   * 🛑 `informational` is not a fault at all. Throttling means the platform paced us; it
   *    clears on its own, and if it does not, our pacing is the thing to fix. Presenting it
   *    as a problem puts a task in someone's queue that they can neither action nor close.
   */
  audience: 'client' | 'support' | 'informational';
  /** MISSING / MISCONFIGURED / … — the one-line verdict, never a category name. */
  verdict: string;
  /** Plain-language cause, for someone who does not know Azure. */
  why: string;
  /** Copy-pasteable commands, in order. Empty when the fix is genuinely portal-only. */
  commands: string[];
  /** Steps that no command can perform. */
  portalSteps: string[];
  /**
   * False when the recipe is inferred from the cause category alone rather than read from
   * the platform's own error text. Surfaced, so nobody treats a guess as a finding.
   */
  fromEvidence: boolean;
  /**
   * Whether any error text was captured at all — INDEPENDENT of whether it drove the
   * verdict.
   *
   * 🛑 Conflating the two put a contradiction on screen: the panel printed "the platform
   *    did not return a message specific enough to name the exact fault" directly above
   *    `AttributeError: 'ContainerAppsOperations' object has no attribute 'revisions'`.
   *    The platform had been extremely specific; the recipe simply had no signature for
   *    it. "No message" and "a message I don't recognise" are different statements, and
   *    only one of them was true.
   */
  hasEvidence: boolean;
  /** Values that could not be resolved from the failure and must be filled in. */
  placeholders: string[];
}

/** Anything the caller knows about the failing run, used to make a command concrete. */
export interface FixContext {
  stage: string;
  causeCategory: string;
  affectedScope: string;
  identityRef: string;
  errorSamples?: string[];
  /** 'dev' | 'prod' — used for the -Environment switch on the project scripts. */
  environment?: string;
}

const PLACEHOLDER_SP_OBJECT_ID = '<service-principal-object-id>';
const PLACEHOLDER_APP_ID = '<app-registration-client-id>';

/** A GUID-shaped identity can go straight into a command; a name cannot. */
const isGuid = (v: string): boolean =>
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(v.trim());

/** Recover an ARM scope from the scope label when it names one. */
function armScope(affectedScope: string): string | null {
  const s = affectedScope.trim();
  if (s.startsWith('/subscriptions/')) return s;
  const m = s.match(/subscription[/:]?\s*([0-9a-f-]{36})/i);
  if (m) return `/subscriptions/${m[1]}`;
  if (isGuid(s)) return `/subscriptions/${s}`;
  return null;
}

/** The subscription an ARM scope sits in, when the scope names one. */
function subscriptionOf(scope: string | null): string | null {
  const m = scope?.match(/^\/subscriptions\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})(\/|$)/i);
  return m ? m[1] : null;
}

const envSwitch = (env?: string) => (env === 'prod' ? 'prod' : 'dev');

/**
 * Which project script owns this surface.
 *
 * 🛑 Named in preference to a raw `az` line. The scripts carry the guardrails — prod
 *    confirmation, endpoint selection, secret resolution — and every hand-run command
 *    bypasses all of them. A client following a bare `az` recipe can get the grant right
 *    and the surrounding state wrong.
 */
function scriptForStage(stage: string, env: string | undefined, assignee: string, subscription: string): string | null {
  const s = stage.toLowerCase();
  const e = envSwitch(env);
  // Each script is printed with every target it takes: its mandatory -Environment, the identity, and the scope's own
  // subscription where the script grants on one. Without -SubscriptionIds, Grant-MonitoringReader grants in the
  // platform's own subscription (its lines 46-48) and reports OK; Grant-CapacityMetricsSPAccess refuses without
  // -SubscriptionId (G3).
  if (s.includes('warehouse')) return `.\\scripts\\Grant-WarehouseSqlAccess.ps1 -Environment ${e}`;
  if (s.includes('capacity_metric') || s.includes('capacity_telemetry'))
    return `.\\scripts\\Grant-CapacityMetricsSPAccess.ps1 -Environment ${e} -SubscriptionId ${subscription}`;
  if (s.includes('cost') || s.includes('billing'))
    return `.\\scripts\\Grant-CostManagementReader.ps1 -Environment ${e} -SubscriptionId ${subscription}`;
  if (s.includes('scanner') || s.includes('fabric') || s.includes('workspace') || s.includes('semantic'))
    return `.\\scripts\\Grant-FabricControlSPWorkspaceAccess.ps1 -ServicePrincipalObjectId ${assignee}`;
  if (s.includes('azure_infra') || s.includes('storage') || s.includes('resource'))
    return `.\\scripts\\Grant-MonitoringReader.ps1 -Environment ${e} -CollectionSpObjectId ${assignee} -SubscriptionIds ${subscription}`;
  return null;
}

const has = (text: string, needle: string) => text.toLowerCase().includes(needle.toLowerCase());

/**
 * Build the fix, then enforce the audience contract on the way out.
 *
 * 🛑 A RECIPE THAT IS NOT THE CUSTOMER'S TO ACT ON CARRIES NO COMMANDS. Owner's rule:
 *    "We only show details when it is something the user can influence with configuration
 *    changes. Everything else will be escalated to the development team."
 *
 *    Enforced HERE rather than branch-by-branch because there are a dozen return sites and
 *    the next one added would quietly reintroduce the leak. The UI also gates on audience,
 *    but a recipe is a value that anything may render — the guarantee belongs to the value,
 *    not to one consumer of it. `placeholders` goes with the commands it annotates.
 */
export function fixRecipeFor(ctx: FixContext): FixRecipe {
  const recipe = buildFixRecipe(ctx);
  if (recipe.audience === 'client') return recipe;
  return { ...recipe, commands: [], placeholders: [] };
}

const LEAVE_IT_OFF = 'Or leave it off: nothing else depends on it.';

/** The optional areas the worker names (collection-worker clients.py), each with the grant that turns it on. */
const OPTIONAL_GRANTS: Record<string, { verdict: string; area: string; step: string }> = {
  'capacity-metrics': {
    verdict: 'OPTIONAL — the Capacity Metrics app is not available to the worker.',
    area: 'Capacity CU figures come from the Fabric Capacity Metrics app',
    step: "Install the Fabric Capacity Metrics app, add the worker's identity as a Viewer of its workspace, and give it " +
      "Build on the app's semantic model.",
  },
  'cost-management': {
    verdict: 'OPTIONAL — Azure cost is not granted to the worker.',
    area: 'Azure cost comes from Cost Management',
    step: "Grant the worker's identity Cost Management Reader on each subscription whose costs it should report. If " +
      'Fabric Control reports a billing account for your organisation, a read-only billing role on it (Billing account ' +
      'reader) instead.',
  },
  'gateway-datasources': {
    verdict: "OPTIONAL — a gateway's data sources are not readable by the worker.",
    area: "A gateway's data sources are read as a user of that gateway",
    step: "Add the worker's identity as a user of the on-premises gateway whose data sources it should list.",
  },
};

/**
 * The fix for a grant the worker named, or null for a label this file does not know (the caller then falls back on the
 * category). Every one is the client's: a grant only their administrators can make.
 */
function notGrantedRecipe(label: string | undefined, assignee: string, fromEvidence: boolean, hasEvidence: boolean): FixRecipe | null {
  const optional = label ? OPTIONAL_GRANTS[label] : undefined;
  if (optional || !label) {
    return {
      audience: 'client',
      verdict: optional?.verdict ?? 'OPTIONAL — an area your organisation has not granted to the worker.',
      why: `Nothing is broken: the worker collects everything else. ${optional?.area ?? 'This area'} is optional, and ` +
        'it stays out until the grant is made.',
      commands: [],
      portalSteps: [optional?.step ?? 'The preflight (Invoke-FabricPreflight.ps1) names the grant for each optional area.', LEAVE_IT_OFF],
      fromEvidence,
      hasEvidence,
      placeholders: [],
    };
  }
  if (label === 'read-only-admin-apis') {
    return {
      audience: 'client',
      verdict: "MISSING — the worker's identity may not call the Fabric read-only admin APIs.",
      why: 'The worker reads your Fabric and Power BI inventory through those APIs; refused, none of it is collected. ' +
        'This does not self-correct.',
      commands: [],
      portalSteps: [
        "In the Fabric admin portal, Tenant settings, Admin API settings: add the worker's identity to a security group " +
          "allowed for 'Service principals can access read-only admin APIs' and not for 'Service principals can access " +
          "admin APIs used for updates'. Changes can take a while to apply; the preflight checks it.",
      ],
      fromEvidence,
      hasEvidence,
      placeholders: [],
    };
  }
  if (label === 'azure-reader') {
    const placeholders = ['<subscription-id>'];
    if (assignee === PLACEHOLDER_SP_OBJECT_ID) placeholders.unshift(PLACEHOLDER_SP_OBJECT_ID);
    return {
      audience: 'client',
      verdict: "MISSING — the worker's identity holds no Reader role on a subscription.",
      why: 'Azure answered with no subscription visible to the worker, so no Azure resource is collected. Reader is ' +
        'read-only: it changes nothing.',
      commands: [
        `az role assignment create --assignee-object-id ${assignee} --assignee-principal-type ServicePrincipal ` +
          '--role "Reader" --scope /subscriptions/<subscription-id> --subscription <subscription-id>',
      ],
      portalSteps: [],
      fromEvidence,
      hasEvidence,
      placeholders,
    };
  }
  return null;
}

/**
 * Build the fix. Evidence first, category second, and honest about which one was used.
 */
function buildFixRecipe(ctx: FixContext): FixRecipe {
  const text = (ctx.errorSamples ?? []).join('   ');
  const hasEvidence = Boolean(text.trim());
  const identity = ctx.identityRef?.trim() || '';
  const assignee = isGuid(identity) ? identity : PLACEHOLDER_SP_OBJECT_ID;
  const placeholders: string[] = [];
  if (assignee === PLACEHOLDER_SP_OBJECT_ID) placeholders.push(PLACEHOLDER_SP_OBJECT_ID);

  // ---- a grant the worker named (Spec 286 T135): `not-granted:<grant>` --------------
  //    The worker labels a refusal it can tell apart; the label is the evidence. An optional area is the client's
  //    choice and nothing is broken (owner, 2026-10-18: recorded, no alert); a required grant names its step.
  const label = /not-granted:([a-z-]+)/.exec(text)?.[1];
  if (label || ctx.causeCategory === 'not_granted') {
    const named = notGrantedRecipe(label, assignee, Boolean(label), hasEvidence);
    if (named) return named;
  }

  // ---- credential faults: the secret itself is wrong -------------------------
  if (text && (has(text, 'AADSTS7000215') || has(text, 'invalid client secret'))) {
    return {
      audience: 'support',
      verdict: 'MISCONFIGURED — the client secret is wrong.',
      why:
        'Entra accepted the application but rejected its password. The secret stored in ' +
        'Key Vault is not the one registered on the app registration — almost always ' +
        'because it was rotated on one side only. No amount of retrying will fix this.',
      commands: [
        `az ad app credential reset --id ${PLACEHOLDER_APP_ID} --append --years 1 --query password -o tsv`,
        `az keyvault secret set --vault-name kv-fabric-control-${envSwitch(ctx.environment)} ` +
          `--name sp-client-secret --value "<the-password-printed-above>"`,
      ],
      portalSteps: [],
      fromEvidence: true,
      hasEvidence,
      placeholders: [PLACEHOLDER_APP_ID, '<the-password-printed-above>'],
    };
  }

  if (text && (has(text, 'AADSTS7000222') || /secret.{0,20}expired/i.test(text))) {
    return {
      audience: 'support',
      verdict: 'MISCONFIGURED — the client secret has expired.',
      why:
        'The credential is past its expiry date. Entra will reject every request until a ' +
        'new secret is issued and stored where the collector reads it.',
      commands: [
        `az ad app credential reset --id ${PLACEHOLDER_APP_ID} --append --years 1 --query password -o tsv`,
        `az keyvault secret set --vault-name kv-fabric-control-${envSwitch(ctx.environment)} ` +
          `--name sp-client-secret --value "<the-password-printed-above>"`,
      ],
      portalSteps: [],
      fromEvidence: true,
      hasEvidence,
      placeholders: [PLACEHOLDER_APP_ID, '<the-password-printed-above>'],
    };
  }

  // 🛑 CONDITIONAL ACCESS — checked BEFORE the generic secret branch. Found live on dev
  //    2026-08-01: AADSTS53003, 718 occurrences. It is neither a bad credential nor a
  //    missing role, and the category alone ('auth') would have sent an operator to Key
  //    Vault to rotate a secret that was never the problem. That wrong turn is worse
  //    than silence: the real gap survives with someone convinced it was handled.
  if (text && (has(text, 'AADSTS53003') || has(text, 'blocked by Conditional Access'))) {
    // 🛑 Entra names the offending policy in the error's `capolids` claim. Naming it
    //    beats "go find the policy": on a tenant with dozens, the search is the whole
    //    job, and guessing the wrong one gets an unrelated policy weakened.
    //    Matched as "GUIDs appearing shortly after `capolids`" rather than by parsing
    //    the claim: the payload arrives with its inner JSON backslash-escaped
    //    (`\"values\":[\"…\"]`), so a quote-anchored pattern silently finds nothing —
    //    which looks exactly like an error that carried no policy at all.
    const capolids = /capolids/i.exec(text);
    const policyIds = capolids
      ? Array.from(
          new Set(
            (
              text
                .slice(capolids.index, capolids.index + 400)
                .match(/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi) ?? []
            ).map((g) => g.toLowerCase()),
          ),
        )
      : [];
    const named = policyIds.length > 0;
    return {
      audience: 'support',
      verdict: named
        ? `MISSING — Conditional Access policy ${policyIds[0]} is blocking this identity.`
        : 'MISSING — a Conditional Access policy is blocking this identity.',
      why:
        'Entra refused to issue a token because a Conditional Access policy denied the ' +
        'sign-in — typically one requiring multi-factor authentication, a compliant ' +
        'device, or blocking legacy/non-interactive authentication. ' +
        '🛑 Removing MFA from the account does NOT exempt it: the policy is evaluated on ' +
        'every sign-in, and a non-interactive flow (a service principal, or a ' +
        'username/password service account) can never present the interactive proof the ' +
        'policy demands. The credential is valid and its Azure roles are irrelevant here ' +
        '— the block happens before either is considered, so rotating the secret will ' +
        'not help. The identity must be EXCLUDED from the policy.',
      commands: named
        ? policyIds.map(
            (id) =>
              `az rest --method GET --url "https://graph.microsoft.com/v1.0/identity/` +
              `conditionalAccess/policies/${id}"`,
          )
        : [
            'az rest --method GET --url ' +
              '"https://graph.microsoft.com/v1.0/identity/conditionalAccess/policies?' +
              '$select=id,displayName,state" -o table',
          ],
      portalSteps: [
        named
          ? `Entra admin centre → Protection → Conditional Access → Policies → open ${policyIds[0]}.`
          : 'Entra admin centre → Protection → Conditional Access → Policies.',
        'Add this identity to the policy\'s EXCLUDED users / service principals — or to ' +
          'an exclusion group the policy already excludes.',
        'If the identity is a service principal, check Conditional Access → Workload ' +
          'identities as well; workload-identity policies are listed separately.',
        '🛑 Requires a Conditional Access Administrator. Until it is changed, every call ' +
          'against this scope fails and the data behind it stays uncollected.',
      ],
      fromEvidence: true,
      hasEvidence,
      placeholders: [],
    };
  }

  if (text && (has(text, 'AADSTS700016') || has(text, 'application with identifier'))) {
    return {
      audience: 'client',
      verdict: 'MISCONFIGURED — the application does not exist in this directory.',
      why:
        'The client id being presented is not registered in the tenant it is ' +
        'authenticating against. The client id and tenant id are not a matching pair.',
      // Every command names the subscription it asks in: a copied command otherwise runs in the az CLI's default
      // subscription. Not az ad: it takes the default subscription's tenant whatever is named (G3), so the directory
      // is asked through Microsoft Graph with a token for the named subscription, kept in a variable, never printed.
      commands: [
        `az account show --subscription <subscription-id> --query "{tenantId:tenantId, subscription:name}" -o table`,
        '$token = az account get-access-token --subscription <subscription-id> --resource-type ms-graph --query accessToken -o tsv',
        `Invoke-RestMethod -Uri 'https://graph.microsoft.com/v1.0/servicePrincipals(appId=''${PLACEHOLDER_APP_ID}'')` +
          `?$select=displayName,appId,id' -Headers @{ Authorization = "Bearer $token" }`,
      ],
      portalSteps: [
        'The first command shows the tenant that subscription is in; the last asks that tenant\'s directory for the ' +
          'application, and answers 404 when it is not registered there. If the app registration lives in a ' +
          'different tenant, correct the configured tenant id rather than creating a second registration.',
      ],
      fromEvidence: true,
      hasEvidence,
      placeholders: [PLACEHOLDER_APP_ID, '<subscription-id>'],
    };
  }

  if (text && (has(text, 'AADSTS65001') || has(text, 'consent'))) {
    return {
      audience: 'client',
      verdict: 'MISSING — nobody has granted admin consent.',
      why:
        'The application is registered and asking for a permission that has never been ' +
        'approved. Only a directory administrator can approve it; the collector cannot.',
      // Not az ad app permission admin-consent: az ad acts in the tenant of the az CLI's default subscription whatever
      // is named (G3). The script is printed with the directory it acts in and only its consent step: its -TenantId
      // defaults to the platform's own tenant, and beyond consent it grants roles on the platform's subscriptions. The
      // portal and the consent URL both name the directory (Microsoft, "Grant tenant-wide admin consent to an
      // application").
      commands: [
        '.\\scripts\\Setup-CollectionJobPermissions.ps1 -TenantId <your-tenant-id> -SkipRBAC -SkipWorkspaceAccess -WhatIf',
        '.\\scripts\\Setup-CollectionJobPermissions.ps1 -TenantId <your-tenant-id> -SkipRBAC -SkipWorkspaceAccess',
      ],
      portalSteps: [
        'Sign in to the Microsoft Entra admin center as at least a Cloud Application Administrator (a Privileged ' +
          'Role Administrator for Microsoft Graph application permissions), browse to Entra ID → Enterprise apps → ' +
          'All applications, select the application, select Permissions under Security, review them, and select ' +
          'Grant admin consent.',
        'Or, with your tenant ID and the app registration\'s application (client) ID, open ' +
          `https://login.microsoftonline.com/<your-tenant-id>/adminconsent?client_id=${PLACEHOLDER_APP_ID} ` +
          'as that administrator.',
      ],
      fromEvidence: true,
      hasEvidence,
      // The placeholders go with the commands they annotate; the URL's are named in its own step.
      placeholders: ['<your-tenant-id>'],
    };
  }

  // ---- Fabric / Power BI tenant settings: no API can do this -----------------
  if (
    text &&
    (has(text, 'PowerBINotAuthorizedException') ||
      has(text, 'tenant setting') ||
      has(text, 'API is not accessible for application'))
  ) {
    return {
      audience: 'client',
      verdict: 'MISSING — a Fabric tenant setting blocks service principals.',
      why:
        'The identity is permitted in Azure, but the Fabric/Power BI tenant settings do ' +
        'not allow service principals to call the admin APIs. Azure roles cannot override ' +
        'this — it is a separate switch owned by the Fabric administrator.',
      commands: ['.\\scripts\\Grant-PowerBIAdminAPIAccess.ps1 -DryRun', '.\\scripts\\Grant-PowerBIAdminAPIAccess.ps1'],
      portalSteps: [
        'Fabric admin portal → Tenant settings → Developer settings → ' +
          '"Service principals can use Fabric APIs".',
        'Fabric admin portal → Tenant settings → Admin API settings → ' +
          '"Service principals can access read-only admin APIs" — enable for a security ' +
          'group that contains the collection identity.',
        '🛑 There is no API for these switches. They must be set in the portal by a ' +
          'Fabric administrator, and collection of this data stays at zero until they are.',
      ],
      fromEvidence: true,
      hasEvidence,
      placeholders: [],
    };
  }

  // ---- the merge refused to empty a table -------------------------------------
  //
  // 🛑 The merge's empty/collapsed-source guard: collection staged 0 (or far too few) rows for a table
  //    that dbo holds thousands of, so the merge kept dbo as it was rather than read the gap as a mass
  //    deletion. Nothing failed in the tenant and nothing is missing from it — this run's collection
  //    produced nothing for the table. It read as UNDIAGNOSED, and for semantic_model_columns as
  //    "Azure says it does not exist", because the row count 52404 contains "404" (owner, 2026-10-05).
  const guard = /MergeGuardTripped:.*?collection\.(\w+)=(\d+) vs dbo\.\w+=(\d+)/i.exec(text);
  if (guard) {
    const [, table, staged, current] = guard;
    return {
      audience: 'support',
      verdict: `NOT A CONFIGURATION PROBLEM — the merge kept the existing data for ${table}.`,
      why:
        `This run's collection staged ${staged} rows for ${table} while the database holds ${current} rows. ` +
        'Replacing them would have read as everything being deleted, so the merge kept the existing data and ' +
        'did not update this table. The data shown is from the last good run. Nothing in your tenant needs ' +
        'to change — the collection that should have produced these rows is ours to fix.',
      commands: [],
      portalSteps: [
        'No action required from you. If this appears on several runs in a row, send it to your platform ' +
          'support contact — it names the table whose collection came back empty.',
      ],
      fromEvidence: true,
      hasEvidence,
      placeholders: [],
    };
  }

  // ---- Azure RBAC: authenticated, but holds no role --------------------------
  const scope = armScope(ctx.affectedScope);
  // ---- our own inventory disagrees with Azure ---------------------------------
  //
  // 🛑 WE ONLY EVER ASK ABOUT RESOURCES INFRASTRUCTURE SYNC COLLECTED. So an entity that
  //    is in our work-list and cannot be found is not a customer problem and not really a
  //    "missing resource" either — it is our own two passes disagreeing. Either infra sync
  //    recorded something that was never right, or it has stopped refreshing and we are
  //    working from stale inventory. Owner: "at the very least, this should not happen."
  //
  //    It was rendering as UNDIAGNOSED, which was wrong twice: the message says plainly
  //    what happened, and the fault is ours. Worse, an operator reading "undiagnosed"
  //    against their storage account has every reason to go looking at permissions.
  if (text && /\bnot found\b/i.test(text) && /NonRetryableError|Storage account|Lakehouse|Warehouse/i.test(text)) {
    const named = /(?:Storage account|Lakehouse|Warehouse|Capacity)\s+(\S+)\s+not found/i.exec(text)?.[1];
    return {
      audience: 'support',
      verdict: named
        ? `NOT A CONFIGURATION PROBLEM — our inventory lists ${named}, Azure does not return it.`
        : 'NOT A CONFIGURATION PROBLEM — our inventory and Azure disagree.',
      why:
        'This collector only asks about resources our own infrastructure sync already ' +
        'recorded, so the two should never disagree. Either the inventory captured ' +
        'something incorrectly or it has stopped refreshing and we are working from a ' +
        'stale list. Nothing in your tenant needs to change — and note we cannot yet tell ' +
        'a deleted resource from one we are not permitted to see, because the underlying ' +
        'client discards the real error. Both are ours to fix.',
      commands: [],
      portalSteps: [
        'No action required from you. If this keeps appearing, send it to your platform ' +
          'support contact — it names the exact resource and points at our inventory.',
      ],
      fromEvidence: true,
      hasEvidence,
      placeholders: [],
    };
  }

  // ---- the target is gone ----------------------------------------------------
  //
  // 🛑 NOT THE CUSTOMER'S TO FIX, so no commands are offered. Observed on dev: an ARM 404
  //    for `Microsoft.App/jobs/fabric-collection-prod` — one of OUR OWN container jobs —
  //    and a OneLake 404 for a `Tables?resource=filesystem` path OUR collector built. Both
  //    are our stale work-list, not their configuration. An earlier version of this branch
  //    handed over `az resource show`, which would have had an operator inspecting
  //    infrastructure to chase a fault that was never on their side.
  //
  //    It is still worth naming precisely, because "404" alone reads as a permission
  //    problem: Azure returns 404 rather than 403 when an identity cannot see a resource
  //    at all, so the two are indistinguishable from the status code and only the support
  //    route can tell them apart.
  // A status code, not a substring: "52404 rows" is not an HTTP 404.
  if (text && (has(text, 'ResourceNotFound') || /\b404\b/.test(text))) {
    const armMatch = /The Resource '([^']+)' under resource group '([^']+)'/i.exec(text);
    const resource = armMatch?.[1];
    // 🛑 TWO DIFFERENT FAULTS WEAR THE SAME STATUS CODE, and only one of them is a
    //    resource that went away. A 404 on a URL the COLLECTOR ASSEMBLED — a OneLake
    //    path, a REST route with ids we substituted — means we asked the wrong question;
    //    the object may be perfectly healthy. Reported verbatim: "if it is a true 404,
    //    then we didn't connect the dots correctly." Calling that "no longer exists"
    //    sends everyone looking for a deletion that never happened.
    const url = /for url '([^']+)'/i.exec(text)?.[1];
    const weBuiltTheUrl = Boolean(url);
    return {
      audience: 'support',
      verdict: weBuiltTheUrl
        ? 'NOT A CONFIGURATION PROBLEM — the collector requested a path that does not exist.'
        : resource
          ? `NOT A CONFIGURATION PROBLEM — ${resource} is in our work-list but no longer exists.`
          : 'NOT A CONFIGURATION PROBLEM — the collector asked for something that is not there.',
      why: weBuiltTheUrl
        ? 'The collector built this URL itself and the service returned 404, so the most ' +
          'likely cause is that we assembled it wrongly — a wrong identifier, or a path ' +
          'that does not apply to this item — rather than something of yours being ' +
          'missing. Nothing in your tenant needs to change; this is ours to correct.'
        : 'The collector went looking for an object that Azure says does not exist. That is ' +
          'our inventory being out of date, not a permission you are missing — nothing in ' +
          'your tenant needs to change. It usually clears itself once the inventory is ' +
          're-collected; if it keeps recurring the work-list is orphaned and we need to fix ' +
          'it at our end.',
      commands: [],
      portalSteps: [
        'No action required from you. Send the error text above to your platform support ' +
          'contact if it persists across several runs — it identifies the exact object.',
      ],
      fromEvidence: true,
      hasEvidence,
      placeholders: [],
    };
  }

  // 🛑 ORDER MATTERS: EVERY TEXT SIGNATURE IS TESTED BEFORE ANY CATEGORY FALLBACK.
  //    The branches below fall back on `ctx.causeCategory === ...` when there is no
  //    matching text, which means a category-only guess would otherwise outrank a message
  //    that says exactly what happened. Caught by test: a 404 naming
  //    `Microsoft.App/jobs/fabric-collection-prod` was answered with "the credential was
  //    rejected (401)" — a confident, wrong, and customer-directed remedy derived from a
  //    category while the evidence sat unread two branches below.

  if (
    (text && (has(text, 'RBACAccessDenied') || has(text, 'does not have authorization') || has(text, 'AuthorizationFailed'))) ||
    ctx.causeCategory === 'authorization'
  ) {
    const fromEvidence = Boolean(
      text && (has(text, 'RBACAccessDenied') || has(text, 'does not have authorization') || has(text, 'AuthorizationFailed')),
    );
    if (!scope) placeholders.push('<scope>');
    // The scope's own subscription, named on both commands: a copied command otherwise runs in the az CLI's default
    // subscription, and --assignee would resolve the principal through Microsoft Graph in the default tenant (G3).
    const subscription = subscriptionOf(scope) ?? '<subscription-id>';
    if (subscription === '<subscription-id>') placeholders.push('<subscription-id>');
    const script = scriptForStage(ctx.stage, ctx.environment, assignee, subscription);
    return {
      audience: 'client',
      verdict: 'MISSING — the identity is valid but holds no role on this scope.',
      why:
        `The credential authenticated successfully and was then denied on ${ctx.affectedScope}. ` +
        'Nothing behind that scope is being collected — the data is not absent, it was ' +
        'never visible. This does not self-correct.',
      commands: [
        `az role assignment create --assignee-object-id ${assignee} --assignee-principal-type ServicePrincipal ` +
          `--role "Reader" --scope ${scope ?? '<scope>'} --subscription ${subscription}`,
        ...(script ? [script] : []),
        `az role assignment list --assignee-object-id ${assignee} --scope ${scope ?? '<scope>'} ` +
          `--subscription ${subscription} --fill-principal-name false -o table`,
      ],
      portalSteps: [],
      fromEvidence,
      hasEvidence,
      placeholders,
    };
  }

  // ---- 401 with no distinguishing evidence -----------------------------------
  if (ctx.causeCategory === 'auth') {
    return {
      audience: 'support',
      verdict: 'MISCONFIGURED — the credential itself was rejected (401).',
      why:
        'This is the credential, not its permissions: Entra refused to issue a token at ' +
        'all. The usual cause is a client secret that expired or was rotated without ' +
        'updating Key Vault. The exact Entra code is in the stored diagnostics — with it, ' +
        'this page can name the precise fault.',
      commands: [
        `az ad app credential list --id ${PLACEHOLDER_APP_ID} --query "[].{ends:endDateTime}" -o table`,
        `az keyvault secret show --vault-name kv-fabric-control-${envSwitch(ctx.environment)} ` +
          `--name sp-client-secret --query "attributes.updated" -o tsv`,
      ],
      portalSteps: [
        'If the credential expiry is in the past, reset it and write the new value to Key Vault.',
      ],
      fromEvidence: false,
      hasEvidence,
      placeholders: [PLACEHOLDER_APP_ID],
    };
  }

  // ---- everything that is NOT a permission problem ---------------------------
  if (ctx.causeCategory === 'throttled') {
    return {
      audience: 'informational',
      verdict: 'INFORMATIONAL — the platform paced us; no action needed.',
      why:
        'The service asked the collector to slow down. This clears by itself. If it ' +
        'persists across days it is a capacity problem, not a permission one.',
      commands: [],
      portalSteps: [],
      fromEvidence: false,
      hasEvidence,
      placeholders: [],
    };
  }

  // 🛑 A Python exception type in the message is DEFINITIVE, not unmatched. An
  //    AttributeError/TypeError/KeyError raised inside the collector can only mean our
  //    code called something wrong — no tenant setting produces one. Treating it as
  //    "no recognisable signature" is what printed "the platform did not return a
  //    message specific enough" directly above
  //    `AttributeError: 'ContainerAppsOperations' object has no attribute 'revisions'`,
  //    which named the fault exactly. The screen contradicted itself.
  const codeFault = /\b(AttributeError|TypeError|KeyError|IndexError|NameError)\b/.exec(text);
  if (codeFault) {
    return {
      audience: 'support',
      verdict: `NOT A CONFIGURATION PROBLEM — a defect in the collector (${codeFault[1]}).`,
      why:
        `The collector raised a ${codeFault[1]} while handling the response. That can ` +
        'only come from our code calling something incorrectly — no permission, role or ' +
        'tenant setting can produce it, and none can fix it. Nothing you change on your ' +
        'side will help, and changing something risks weakening your posture for nothing.',
      commands: [],
      portalSteps: [
        'Send the error text below to your platform support contact — it names the exact ' +
          'call that is wrong, which is what a fix needs.',
      ],
      fromEvidence: true,
      hasEvidence,
      placeholders: [],
    };
  }

  if (ctx.causeCategory === 'schema') {
    return {
      audience: 'support',
      verdict: 'NOT A CONFIGURATION PROBLEM — the API changed shape.',
      why:
        'The response did not match what the collector expects. This is a defect in the ' +
        'collector, not in your configuration — no permission, role or tenant setting ' +
        'will fix it, and changing one risks weakening your posture for no benefit.',
      // 🛑 The BLAME ALLOCATION is the point and stays: a client told only "schema
      //    error" starts hunting a permission that was never missing, and may loosen a
      //    policy to chase it. Saying plainly that it is ours is what prevents that.
      //
      //    🛑 But the VENDOR NAME does not belong in a source file. This ships to every
      //    tenant, support routing differs per deployment, and Principle XXIII puts
      //    configuration in the database. Named support contact is a settings-backed
      //    field, not a string literal — until it exists, say the true thing generically.
      commands: [],
      portalSteps: [
        'Send the error text above to your platform support contact. It identifies the ' +
          'exact call that changed, which is what a fix needs.',
      ],
      fromEvidence: false,
      hasEvidence,
      placeholders: [],
    };
  }

  return {
    audience: 'support',
    verdict: 'UNDIAGNOSED — the error matched no known signature.',
    why:
      'The failure was recorded honestly rather than guessed at. Guessing between a ' +
      'missing grant and a bad credential would send you to change something that was ' +
      'never broken, leaving the real gap in place.',
    commands: [],
    portalSteps: [
      'Open the stored diagnostics for the exact message and send it to your platform ' +
        'support contact — a recurring shape belongs in the classifier.',
    ],
    fromEvidence: false,
    hasEvidence,
    placeholders: [],
  };
}

export default fixRecipeFor;
